/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  area as turfArea,
  bbox,
  bboxClip,
  booleanPointInPolygon,
  featureCollection,
  intersect,
  length as turfLength,
  along,
  lineSplit,
  polygonToLine,
  union,
} from '@turf/turf';
import type {
  Feature,
  FeatureCollection,
  Geometry,
  LineString,
  MultiPolygon,
  Polygon,
  Position,
} from 'geojson';
import { isEqual, omit } from 'lodash';
import { buffer, getHeight, getWidth, intersects } from 'ol/extent';
import { layerUtils } from '@opengeoweb/store';
import {
  isGeoJSONGeometryEmpty,
  roundFeatureCoordinates,
} from '@opengeoweb/webmap-react';
import type {
  DrawingListItem,
  DrawingTransformation,
} from '../../store/warningsDrawings/types';

export type MaskPolygon = Feature<Polygon | MultiPolygon>;

export const CLIPPED_RESULT_FILL_OPACITY = 0.4;
export const MASKED_FILL_OPACITY_KEY = 'maskedFillOpacity';

export const getDrawnFeatureIndexes = (geoJSON: FeatureCollection): number[] =>
  geoJSON.features.reduce<number[]>(
    (indexes, feature, index) =>
      isGeoJSONGeometryEmpty(feature) ? indexes : [...indexes, index],
    [],
  );

export const getFeatureIndexesToHideFill = (
  geoJSON: FeatureCollection,
): number[] =>
  getDrawnFeatureIndexes(geoJSON).filter(
    (index) => geoJSON.features[index].properties?.['fill-opacity'] !== 0,
  );

export const getFeatureIndexesToRestoreFill = (
  geoJSON: FeatureCollection,
): number[] =>
  geoJSON.features.reduce<number[]>(
    (indexes, feature, index) =>
      feature.properties?.[MASKED_FILL_OPACITY_KEY] !== undefined
        ? [...indexes, index]
        : indexes,
    [],
  );

export const restoreMaskedFillProperties = (
  properties: GeoJSON.GeoJsonProperties,
): GeoJSON.GeoJsonProperties => {
  if (properties?.[MASKED_FILL_OPACITY_KEY] === undefined) {
    return properties;
  }
  const { [MASKED_FILL_OPACITY_KEY]: fillOpacity, ...restProperties } =
    properties;
  return fillOpacity === null
    ? omit(restProperties, 'fill-opacity')
    : { ...restProperties, 'fill-opacity': fillOpacity };
};

export const restoreMaskedFill = (
  geoJSON: FeatureCollection,
): FeatureCollection => ({
  ...geoJSON,
  features: geoJSON.features.map((feature) => ({
    ...feature,
    properties: restoreMaskedFillProperties(feature.properties),
  })),
});

type BBox = [number, number, number, number];

interface MaskPart {
  polygon: MaskPolygon;
  bbox: BBox;
}

const maskPolygonCache = new WeakMap<FeatureCollection, MaskPolygon | null>();
const maskPartsCache = new WeakMap<FeatureCollection, MaskPart[]>();
const clippedGeometryCache = new WeakMap<
  Geometry,
  WeakMap<FeatureCollection, Geometry | null>
>();

export const isPolygonFeature = (feature: Feature): feature is MaskPolygon =>
  feature.geometry?.type === 'Polygon' ||
  feature.geometry?.type === 'MultiPolygon';

export const getMaskPolygon = (mask: FeatureCollection): MaskPolygon | null => {
  const cachedMaskPolygon = maskPolygonCache.get(mask);
  if (cachedMaskPolygon !== undefined) {
    return cachedMaskPolygon;
  }
  const polygons = mask.features.filter(isPolygonFeature);
  const maskPolygon =
    polygons.length > 1
      ? (union(featureCollection(polygons)) as MaskPolygon | null)
      : (polygons[0] ?? null);
  maskPolygonCache.set(mask, maskPolygon);
  return maskPolygon;
};

const getMaskParts = (mask: FeatureCollection): MaskPart[] => {
  const cachedMaskParts = maskPartsCache.get(mask);
  if (cachedMaskParts) {
    return cachedMaskParts;
  }
  const maskParts = mask.features
    .filter(isPolygonFeature)
    .map((polygon) => ({ polygon, bbox: bbox(polygon) as BBox }));
  maskPartsCache.set(mask, maskParts);
  return maskParts;
};

const padBBox = (geometryBBox: BBox): BBox =>
  buffer(
    geometryBBox,
    Math.max(getWidth(geometryBBox), getHeight(geometryBBox)) * 0.01 + 1e-6,
  ) as BBox;

const removeEmptyRings = (polygon: MaskPolygon): MaskPolygon => {
  const isRing = (ring: Position[]): boolean => ring.length > 3;
  if (polygon.geometry.type === 'Polygon') {
    return {
      ...polygon,
      geometry: {
        ...polygon.geometry,
        coordinates: polygon.geometry.coordinates.filter(isRing),
      },
    };
  }
  return {
    ...polygon,
    geometry: {
      ...polygon.geometry,
      coordinates: polygon.geometry.coordinates
        .map((rings) => rings.filter(isRing))
        .filter((rings) => rings.length > 0),
    },
  };
};

const getMaskPolygonInBBox = (
  maskPolygon: MaskPolygon,
  maskBBox: BBox,
  geometryBBox: BBox,
): MaskPolygon | null => {
  if (!intersects(maskBBox, geometryBBox)) {
    return null;
  }
  const clippedMask = removeEmptyRings(
    bboxClip(maskPolygon, padBBox(geometryBBox)) as MaskPolygon,
  );
  return clippedMask.geometry.coordinates.length ? clippedMask : null;
};

const isPointInMask = (position: Position, maskPolygon: MaskPolygon): boolean =>
  booleanPointInPolygon(position, maskPolygon);

const clipLine = (
  coordinates: Position[],
  maskPolygon: MaskPolygon,
  splitterPolygon: MaskPolygon,
): Position[][] => {
  const line: Feature<LineString> = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates },
  };
  const outline = polygonToLine(splitterPolygon);
  const outlines =
    outline.type === 'FeatureCollection' ? outline.features : [outline];
  const segments = outlines.reduce<Feature<LineString>[]>(
    (currentSegments, splitter) =>
      currentSegments.flatMap((segment) => {
        const split = lineSplit(segment, splitter);
        return split.features.length ? split.features : [segment];
      }),
    [line],
  );

  return segments
    .filter((segment) => {
      const midpoint = along(segment, turfLength(segment) / 2);
      return isPointInMask(midpoint.geometry.coordinates, maskPolygon);
    })
    .map((segment) => segment.geometry.coordinates);
};

const isPointInAnyMaskPart = (
  position: Position,
  mask: FeatureCollection,
): boolean =>
  getMaskParts(mask).some((part) => isPointInMask(position, part.polygon));

const clipGeometry = (
  geometry: Geometry,
  mask: FeatureCollection,
): Geometry | null => {
  switch (geometry.type) {
    case 'Polygon':
    case 'MultiPolygon': {
      const geometryBBox = bbox(geometry) as BBox;
      const feature: Feature<Polygon | MultiPolygon> = {
        type: 'Feature',
        properties: {},
        geometry,
      };
      const clippedParts = getMaskParts(mask).reduce<MaskPolygon[]>(
        (result, part) => {
          const maskInBBox = getMaskPolygonInBBox(
            part.polygon,
            part.bbox,
            geometryBBox,
          );
          const clippedPart =
            maskInBBox && intersect(featureCollection([feature, maskInBBox]));
          return clippedPart ? [...result, clippedPart] : result;
        },
        [],
      );
      if (clippedParts.length <= 1) {
        return clippedParts[0]?.geometry ?? null;
      }
      return union(featureCollection(clippedParts))?.geometry ?? null;
    }
    case 'LineString':
    case 'MultiLineString': {
      const maskPolygon = getMaskPolygon(mask);
      const maskInBBox =
        maskPolygon &&
        getMaskPolygonInBBox(
          maskPolygon,
          bbox(maskPolygon) as BBox,
          bbox(geometry) as BBox,
        );
      if (!maskPolygon || !maskInBBox) {
        return null;
      }
      const lines =
        geometry.type === 'LineString'
          ? [geometry.coordinates]
          : geometry.coordinates;
      const clipped = lines.flatMap((line) =>
        clipLine(line, maskPolygon, maskInBBox),
      );
      if (clipped.length === 0) {
        return null;
      }
      return clipped.length === 1
        ? { type: 'LineString', coordinates: clipped[0] }
        : { type: 'MultiLineString', coordinates: clipped };
    }
    case 'Point':
      return isPointInAnyMaskPart(geometry.coordinates, mask) ? geometry : null;
    case 'MultiPoint': {
      const points = geometry.coordinates.filter((point) =>
        isPointInAnyMaskPart(point, mask),
      );
      return points.length ? { type: 'MultiPoint', coordinates: points } : null;
    }
    default:
      return geometry;
  }
};

const roundClippedGeometry = (geometry: Geometry): Geometry => {
  if (geometry.type === 'GeometryCollection') {
    return geometry;
  }
  return roundFeatureCoordinates({
    type: 'Feature',
    properties: {},
    geometry,
  }).geometry;
};

const getClippedGeometry = (
  geometry: Geometry,
  mask: FeatureCollection,
): Geometry | null => {
  const cachedByMask =
    clippedGeometryCache.get(geometry) ??
    new WeakMap<FeatureCollection, Geometry | null>();
  if (cachedByMask.has(mask)) {
    return cachedByMask.get(mask)!;
  }
  const clippedGeometry = clipGeometry(geometry, mask);
  const roundedGeometry = clippedGeometry
    ? roundClippedGeometry(clippedGeometry)
    : null;
  cachedByMask.set(mask, roundedGeometry);
  clippedGeometryCache.set(geometry, cachedByMask);
  return roundedGeometry;
};

export const clipGeoJSONWithMask = (
  geoJSON: FeatureCollection,
  mask: FeatureCollection,
): FeatureCollection => {
  if (getMaskParts(mask).length === 0) {
    return { ...geoJSON, features: [] };
  }

  const features = geoJSON.features.reduce<Feature[]>((result, feature) => {
    if (!feature.geometry || isGeoJSONGeometryEmpty(feature)) {
      return result;
    }
    const geometry = getClippedGeometry(feature.geometry, mask);
    return geometry ? [...result, { ...feature, geometry }] : result;
  }, []);

  return { ...geoJSON, features };
};

export type PolygonClipResult = 'inside' | 'clipped' | 'outside' | 'error';

// relative area difference below which a clipped polygon is considered unchanged
const CLIPPED_AREA_TOLERANCE = 1e-6;

const clipPolygon = (
  feature: MaskPolygon,
  mask: FeatureCollection,
): Feature | undefined => {
  const [clipped] = clipGeoJSONWithMask(
    featureCollection([feature]),
    mask,
  ).features;
  return clipped && isPolygonFeature(clipped) ? clipped : undefined;
};

export type PolygonMaskOverlap = 'overlap' | 'outside' | 'error';

/**
 * Checks whether the polygon overlaps the mask:
 * - overlap: (part of) the polygon lies within the mask
 * - outside: the polygon has no overlap with the mask
 * - error: the polygon could not be clipped (invalid geometry)
 */
export const getPolygonMaskOverlap = (
  feature: MaskPolygon,
  mask: FeatureCollection,
): PolygonMaskOverlap => {
  try {
    return clipPolygon(feature, mask) ? 'overlap' : 'outside';
  } catch {
    return 'error';
  }
};

/**
 * Clips a polygon with the mask and classifies the result:
 * - inside: the polygon lies within the mask and is returned unchanged
 * - clipped: the polygon lies partly within the mask and is clipped to it
 * - outside: the polygon has no overlap with the mask, returned unchanged
 * - error: the polygon could not be clipped, returned unchanged
 * Since the clipped geometry is rounded and its vertices can be reordered, the
 * areas are compared instead of the geometries to detect whether it was changed.
 */
export const classifyPolygonClip = (
  feature: MaskPolygon,
  mask: FeatureCollection,
): { feature: Feature; result: PolygonClipResult } => {
  try {
    const clipped = clipPolygon(feature, mask);
    if (!clipped) {
      return { feature, result: 'outside' };
    }
    const originalArea = turfArea(roundFeatureCoordinates(feature));
    const lostArea = originalArea - turfArea(clipped);
    return lostArea <= originalArea * CLIPPED_AREA_TOLERANCE
      ? { feature, result: 'inside' }
      : { feature: clipped, result: 'clipped' };
  } catch {
    return { feature, result: 'error' };
  }
};

export const findMaskForTransformation = (
  masks: DrawingListItem[],
  transformation?: DrawingTransformation | null,
): DrawingListItem | undefined => {
  if (!transformation) {
    return undefined;
  }
  const getGeometries = (geoJSON: FeatureCollection): Geometry[] =>
    geoJSON.features.map((feature) => feature.geometry);
  const transformationGeometries = getGeometries(transformation.mask);
  return masks.find((mask) =>
    isEqual(getGeometries(mask.geoJSON), transformationGeometries),
  );
};

export const buildClippedHighlightGeoJSON = (
  clipped: FeatureCollection,
): FeatureCollection => ({
  ...clipped,
  features: clipped.features
    // hidden objects are not drawn in the drawing tool, but are still clipped and saved
    .filter((feature) => !layerUtils.isHiddenFeature(feature))
    .map((feature, index) => ({
      ...feature,
      id: `mask-result-${feature.id ?? index}`,
      properties: {
        ...feature.properties,
        isReadonly: true,
        mapFeatureClass: 'default',
        isSelected: false,
        fill: '#186DFF',
        'fill-opacity': CLIPPED_RESULT_FILL_OPACITY,
        'stroke-width': 0,
        'stroke-opacity': 0,
      },
    })),
});
